import React, { useState, useEffect, useRef, startTransition } from 'react';
import { createPortal, flushSync } from 'react-dom';
import SponsorshipFlow from '../sponsorship/SponsorshipFlow';
import PDFViewer from '../PDFViewer';
import { useConfig } from '../../config/ConfigContext';
import { extractVideoId } from '../../services/youtubeService';
import { buildDriveStreamUrl } from '../../utils/driveStreamUrl';
import { getRandomKeyTakeaways } from '../../services/keyTakeawaysService';
import html2canvas from 'html2canvas';
import { Share as ShareIcon } from '@mui/icons-material';
import { DONATION_AMOUNTS, formatDonationAmounts } from '../../constants/donationAmounts';

/**
 * Stories Bar Component (Mobile Only)
 * Instagram-style stories at the top of mobile homepage
 */
const StoriesBar = ({
  announcements,
  currentEvent,
  latestLecture,
  videos = [],
  recentLives = [],
  shorts = [],
  lectureClips = [],
  onStoryClick
}) => {
  const { config, theme } = useConfig();
  const [activeStory, setActiveStory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isModalEntering, setIsModalEntering] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [viewedContent, setViewedContent] = useState({});
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [currentShortIndex, setCurrentShortIndex] = useState(0);
  const [currentClipIndex, setCurrentClipIndex] = useState(0);
  const [currentInsightIndex, setCurrentInsightIndex] = useState(0);

  // Initialize viewed content from localStorage on mount
  useEffect(() => {
    const getViewedContent = () => {
      try {
        const viewed = localStorage.getItem('viewedContent');
        return viewed ? JSON.parse(viewed) : {};
      } catch {
        return {};
      }
    };
    setViewedContent(getViewedContent());
  }, []);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [videoAutoPlay, setVideoAutoPlay] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoMuted, setVideoMuted] = useState(true); // Start muted for autoplay
  const [shortsAutoPlay, setShortsAutoPlay] = useState(false);
  const [shortsProgress, setShortsProgress] = useState(0);
  const [shortsMuted, setShortsMuted] = useState(true); // Start muted for Instagram/TikTok-style autoplay
  const [majlisInsights, setMajlisInsights] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfAnnouncement, setPdfAnnouncement] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const videoTimerRef = useRef(null);
  const shortsTimerRef = useRef(null);
  const PREVIEW_DURATION = 15000; // 15 seconds
  
  // Helper function to detect if URL is a PDF
  const isPdfUrl = (url) => {
    if (!url) return false;
    const urlLower = url.toLowerCase();
    
    // Check for common PDF indicators
    return urlLower.endsWith('.pdf') || 
           urlLower.includes('.pdf?') || 
           urlLower.includes('/pdf/') ||
           urlLower.includes('type=application/pdf') ||
           urlLower.includes('export?format=pdf') ||
           // Google Drive document export as PDF
           (urlLower.includes('docs.google.com') && urlLower.includes('/export?format=pdf')) ||
           // Dropbox PDF files
           (urlLower.includes('dropbox.com') && urlLower.includes('.pdf')) ||
           // Supabase storage with PDF
           (urlLower.includes('supabase') && urlLower.includes('.pdf'));
  };

  // Extract dates from announcement content
  const extractDatesFromAnnouncement = (announcement) => {
    if (!announcement) return [];
    
    const dates = [];
    const textToSearch = [
      announcement.title || '',
      announcement.description || '',
      announcement.content || ''
    ];

    textToSearch.forEach((text) => {
      if (!text) return;
      
      // Pattern to match various date formats
      const datePatterns = [
        /\b(today|tonight|tomorrow)\b/gi,
        /\b(this|next)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gi,
        /\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})\b/g,
        /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+(\d{1,2})(?:st|nd|rd|th)?,?\s*(\d{4})?\b/gi,
      ];
      
      datePatterns.forEach((pattern) => {
        const matches = [...text.matchAll(pattern)];
        for (const match of matches) {
          let date = null;
          const now = new Date();

          if (/today|tonight/i.test(match[0])) {
            date = new Date();
          } else if (/tomorrow/i.test(match[0])) {
            date = new Date();
            date.setDate(date.getDate() + 1);
          } else if (/this|next/i.test(match[0])) {
            const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const targetDay = dayNames.findIndex((d) => match[2] && match[2].toLowerCase() === d);
            if (targetDay !== -1) {
              date = new Date();
              const currentDay = date.getDay();
              let daysToAdd = targetDay - currentDay;
              if (match[1].toLowerCase() === 'next' || daysToAdd <= 0) {
                daysToAdd += 7;
              }
              date.setDate(date.getDate() + daysToAdd);
            }
          } else if (/\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/.test(match[0])) {
            // Handle M/D/YY or M/D/YYYY format
            const parts = match[0].split(/[\/\-]/);
            if (parts.length === 3) {
              const month = parseInt(parts[0]) - 1; // JavaScript months are 0-based
              const day = parseInt(parts[1]);
              let year = parseInt(parts[2]);
              
              if (year < 100) {
                year = year + 2000;
              }
              
              date = new Date(year, month, day);
              
              // Validate the date is reasonable
              if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
                date = null; // Invalid date
              }
            }
          } else {
            // Month name format
            const monthNames = {
              jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
              jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
            };
            const monthStr = match[1].toLowerCase().slice(0, 3);
            const day = parseInt(match[2]);
            const year = match[3] ? parseInt(match[3]) : now.getFullYear();
            if (monthNames[monthStr] !== undefined && day) {
              date = new Date(year, monthNames[monthStr], day);
            }
          }

          if (date && !isNaN(date.getTime())) {
            dates.push(date);
          }
        }
      });
    });

    return dates;
  };

  // Check if announcement is for today or upcoming event
  const isEventTodayOrUpcoming = (announcement) => {
    const extractedDates = extractDatesFromAnnouncement(announcement);
    
    // If no dates found in content, check scheduled_for field
    if (extractedDates.length === 0 && announcement.scheduled_for) {
      extractedDates.push(new Date(announcement.scheduled_for));
    }
    
    // If still no dates, consider it as always eligible
    if (extractedDates.length === 0) {
      return true; // Show sponsor button if no specific date mentioned
    }
    
    // Check if any date is today or in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    
    const isUpcoming = extractedDates.some(date => date >= today);
    
    return isUpcoming;
  };

  // Helper functions for content-based viewed tracking
  const getViewedContentFromStorage = () => {
    try {
      const viewed = localStorage.getItem('viewedContent');
      return viewed ? JSON.parse(viewed) : {};
    } catch {
      return {};
    }
  };

  const markContentAsViewed = (contentType, contentId) => {
    if (!contentId) return;

    const viewed = getViewedContentFromStorage();
    const today = new Date().toDateString();

    if (!viewed[today]) {
      viewed[today] = {};
    }

    if (!viewed[today][contentType]) {
      viewed[today][contentType] = [];
    }

    // Add if not already viewed
    if (!viewed[today][contentType].includes(contentId)) {
      viewed[today][contentType].push(contentId);

      // Clean up old dates (keep last 7 days)
      const dates = Object.keys(viewed).sort();
      if (dates.length > 7) {
        dates.slice(0, -7).forEach(date => delete viewed[date]);
      }

      localStorage.setItem('viewedContent', JSON.stringify(viewed));
      setViewedContent(viewed);
    }
  };

  // Helper function to determine viewing threshold for each content type
  const getViewThreshold = (storyType, totalCount) => {
    switch (storyType) {
      case 'announcement':
        // Mark as viewed after viewing just 1 announcement (users check latest)
        return 1;
      case 'shorts':
        // Mark as viewed after 60% completion (2 of 3)
        return Math.ceil(totalCount * 0.6);
      case 'videos':
      case 'live-videos':
        // Mark as viewed after viewing at least 2 videos (or all if less than 2)
        return Math.min(2, totalCount);
      case 'insights':
        // Mark as viewed after 66% completion (2 of 3)
        return Math.ceil(totalCount * 0.66);
      default:
        // Default: require 60% completion
        return Math.ceil(totalCount * 0.6);
    }
  };

  // Check if content in a story category meets the "viewed" threshold
  const isAllContentViewed = (storyType, storyData) => {
    if (!storyData || storyData.length === 0) return true;

    const today = new Date().toDateString();
    const viewed = viewedContent[today] || {};

    // Map story type to storage key
    const typeMap = {
      'announcement': 'announcements',
      'videos': 'videos',
      'live-videos': 'liveVideos',
      'shorts': 'shorts',
      'insights': 'insights'
    };

    const storageKey = typeMap[storyType] || storyType;
    const viewedIds = viewed[storageKey] || [];

    // Extract all content IDs from story data
    const allIds = storyData.map((item, index) => {
      if (storyType === 'insights') {
        return `insight-${item.lecture_id}-${index}`;
      }
      return item.id || item.videoId || item.video_id;
    }).filter(Boolean);

    // Calculate how many items have been viewed
    const viewedCount = viewedIds.filter(id => allIds.includes(id)).length;

    // Get the threshold for this content type
    const threshold = getViewThreshold(storyType, allIds.length);

    // Return true if viewed count meets or exceeds threshold
    return viewedCount >= threshold;
  };

  const getLighterColor = (color) => {
    // Convert hex colors to lighter versions
    const colorMap = {
      '#f59e0b': '#fbbf24', // amber
      '#dc2626': '#ef4444', // red
      '#3b82f6': '#60a5fa', // blue
      '#10b981': '#34d399', // emerald
      '#8b5cf6': '#a78bfa', // violet
      '#ec4899': '#f472b6', // pink
    };
    return colorMap[color] || color;
  };

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!showModal) return;
    
    // Defer scroll lock to avoid blocking first paint
    const raf = requestAnimationFrame(() => {
      document.body.style.overflow = 'hidden';
    });
    
    return () => { 
      cancelAnimationFrame(raf);
      document.body.style.overflow = ''; 
    };
  }, [showModal]);

  // Check URL parameters to auto-open stories when shared
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const insightParam = params.get('insight');
    
    if (insightParam && insightParam.startsWith('majlis-')) {
      // Parse the insight index from the parameter (format: majlis-0-timestamp)
      const parts = insightParam.split('-');
      const insightIndex = parseInt(parts[1], 10);
      
      if (!isNaN(insightIndex) && majlisInsights.length > 0) {
        // Find the insights story in the stories array
        const insightsStory = stories.find(s => s.type === 'insights');
        if (insightsStory) {
          // Open the modal with the insights story
          setShowModal(true);
          setIsModalEntering(true);
          setActiveStory(insightsStory);
          setModalContent(insightsStory);
          setCurrentInsightIndex(Math.min(insightIndex, majlisInsights.length - 1));

          // Mark the specific insight as viewed
          const targetInsight = majlisInsights[Math.min(insightIndex, majlisInsights.length - 1)];
          if (targetInsight) {
            const contentId = `insight-${targetInsight.lecture_id}-${Math.min(insightIndex, majlisInsights.length - 1)}`;
            markContentAsViewed('insights', contentId);
          }
          
          // Clean up the URL
          const newUrl = new URL(window.location);
          newUrl.searchParams.delete('insight');
          window.history.replaceState({}, '', newUrl);
        }
      }
    }
  }, [majlisInsights, announcements, videos, shorts, recentLives]); // Run when data changes

  // Load Majlis Insights
  useEffect(() => {
    const loadInsights = async () => {
      if (!config?.google_sheet_id) {
        setInsightsLoading(false);
        return;
      }

      try {
        const insights = await getRandomKeyTakeaways(config.google_sheet_id, 3);
        // Map the fields to match what the component expects
        const mappedInsights = insights.map(insight => ({
          takeaway: insight.text,  // Map 'text' to 'takeaway'
          lecture_id: insight.lectureId,  // Map for consistency
          lecture_title: insight.lectureTitle,
          speaker: insight.lectureSpeaker,
          series: insight.lectureSeries
        }));
        setMajlisInsights(mappedInsights);
      } catch (err) {
        console.error('Error loading majlis insights:', err);
      } finally {
        setInsightsLoading(false);
      }
    };

    loadInsights();
  }, [config]);

  // Create stories array from available content
  const stories = [];

  // Add single announcement story containing all announcements
  if (announcements && announcements.length > 0) {
    // Limit to 5 most recent announcements for better UX
    const limitedAnnouncements = announcements.slice(0, 5);

    stories.push({
      id: 'announcements-story',
      type: 'announcement',
      label: 'Announce',
      color: theme?.colors?.primary || '#0f172a',
      data: limitedAnnouncements,
      isNew: true,
      count: limitedAnnouncements.length
    });
  }

  // Add Shorts story if there are shorts
  if (shorts && shorts.length > 0) {
    stories.push({
      id: 'shorts-story',
      type: 'shorts',
      label: 'Latest Shorts',
      color: theme?.colors?.primary || '#0f172a',
      data: shorts.slice(0, 3), // Reduced to 3 shorts for better completion UX
      count: Math.min(shorts.length, 3)
    });
  }

  // Add Lecture Clips story if there are clips
  if (lectureClips && lectureClips.length > 0) {
    stories.push({
      id: 'lecture-clips-story',
      type: 'lecture-clips',
      label: 'Lecture Clips',
      color: theme?.colors?.primary || '#0f172a',
      data: lectureClips.slice(0, 5),
      isNew: true,
      count: Math.min(lectureClips.length, 5)
    });
  }

  // Add Videos story if there are videos
  if (videos && videos.length > 0) {
    // Limit to 5 latest videos for better UX
    const limitedVideos = videos.slice(0, 5);

    stories.push({
      id: 'videos-story',
      type: 'videos',
      label: 'Recent Videos',
      color: theme?.colors?.primary || '#0f172a',
      data: limitedVideos,
      isNew: limitedVideos.some(v => {
        // Check if video is recent (within last 7 days)
        const uploadDate = new Date(v.uploadDate || v.createdAt);
        const daysSince = (new Date() - uploadDate) / (1000 * 60 * 60 * 24);
        return daysSince <= 7;
      }),
      count: limitedVideos.length
    });
  }
  
  // Add Live Videos story if there are live videos
  if (recentLives && recentLives.length > 0) {
    const hasActiveLive = recentLives.some(v => v.isLiveNow);
    stories.push({
      id: 'live-videos-story',
      type: 'live-videos',
      label: hasActiveLive ? 'Live Now' : 'Past Lives',
      color: '#dc2626',
      data: recentLives,
      isLive: hasActiveLive,
      count: recentLives.length
    });
  }

  // Add Majlis Insights story (replacing latest lecture) with 3 key insights
  if (!insightsLoading && majlisInsights.length > 0) {
    stories.push({
      id: 'majlis-insights',
      type: 'insights',
      label: 'Majlis Insights',
      color: theme?.colors?.primary || '#0f172a',
      data: majlisInsights, // Contains 3 insights fetched from getRandomKeyTakeaways
      count: majlisInsights.length
    });
  }

  // Check if event is live
  function isEventLive(event) {
    if (!event) return false;
    const now = new Date();
    const start = new Date(event.start_date + 'T' + (event.start_time || '00:00:00'));
    const end = new Date(event.end_date + 'T' + (event.end_time || '23:59:59'));
    return now >= start && now <= end;
  }

  // Handle swipe gestures
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    // Check story type and handle swipe within that story's items
    if (modalContent?.type === 'insights' && Array.isArray(modalContent?.data)) {
      const insightsCount = modalContent?.data?.length || 0;
      
      if (isLeftSwipe && currentInsightIndex < insightsCount - 1) {
        // Next insight
        setCurrentInsightIndex(currentInsightIndex + 1);
      }
      if (isRightSwipe && currentInsightIndex > 0) {
        // Previous insight
        setCurrentInsightIndex(currentInsightIndex - 1);
      }
    } else if (modalContent?.type === 'announcement' && Array.isArray(modalContent?.data)) {
      const announcementCount = modalContent?.data?.length || 0;
      
      if (isLeftSwipe && currentAnnouncementIndex < announcementCount - 1) {
        // Next announcement (older)
        setCurrentAnnouncementIndex(currentAnnouncementIndex + 1);
      }
      if (isRightSwipe && currentAnnouncementIndex > 0) {
        // Previous announcement (newer)
        setCurrentAnnouncementIndex(currentAnnouncementIndex - 1);
      }
    } else if (modalContent?.type === 'lecture-clips' && Array.isArray(modalContent?.data)) {
      const clipsCount = modalContent?.data?.length || 0;
      if (isLeftSwipe && currentClipIndex < clipsCount - 1) {
        setCurrentClipIndex(currentClipIndex + 1);
      }
      if (isRightSwipe && currentClipIndex > 0) {
        setCurrentClipIndex(currentClipIndex - 1);
      }
    } else if (modalContent?.type === 'shorts' && Array.isArray(modalContent?.data)) {
      const shortsCount = modalContent?.data?.length || 0;

      if (isLeftSwipe && currentShortIndex < shortsCount - 1) {
        // Next short
        setCurrentShortIndex(currentShortIndex + 1);
      }
      if (isRightSwipe && currentShortIndex > 0) {
        // Previous short
        setCurrentShortIndex(currentShortIndex - 1);
      }
    } else if ((modalContent?.type === 'videos' || modalContent?.type === 'live-videos') && Array.isArray(modalContent?.data)) {
      const videosCount = modalContent?.data?.length || 0;
      
      if (isLeftSwipe && currentVideoIndex < videosCount - 1) {
        // Next video
        setCurrentVideoIndex(currentVideoIndex + 1);
        setVideoProgress(0); // Reset progress for new video
        setVideoAutoPlay(false); // Stop autoplay when manually navigating
      }
      if (isRightSwipe && currentVideoIndex > 0) {
        // Previous video
        setCurrentVideoIndex(currentVideoIndex - 1);
        setVideoProgress(0); // Reset progress for new video
        setVideoAutoPlay(false); // Stop autoplay when manually navigating
      }
    } else if (stories.length > 1) {
      // Only navigate between story types if we're at the edge of current story's items
      // or if the story has only one item
      const canNavigate = 
        !Array.isArray(modalContent?.data) || 
        modalContent?.data.length === 1 ||
        (modalContent?.type === 'insights' && 
          ((isLeftSwipe && currentInsightIndex === (modalContent?.data?.length || 0) - 1) ||
           (isRightSwipe && currentInsightIndex === 0))) ||
        (modalContent?.type === 'announcement' && 
          ((isLeftSwipe && currentAnnouncementIndex === (modalContent?.data?.length || 0) - 1) ||
           (isRightSwipe && currentAnnouncementIndex === 0))) ||
        (modalContent?.type === 'shorts' &&
          ((isLeftSwipe && currentShortIndex === (modalContent?.data?.length || 0) - 1) ||
           (isRightSwipe && currentShortIndex === 0))) ||
        (modalContent?.type === 'lecture-clips' &&
          ((isLeftSwipe && currentClipIndex === (modalContent?.data?.length || 0) - 1) ||
           (isRightSwipe && currentClipIndex === 0))) ||
        ((modalContent?.type === 'videos' || modalContent?.type === 'live-videos') && 
          ((isLeftSwipe && currentVideoIndex === (modalContent?.data?.length || 0) - 1) ||
           (isRightSwipe && currentVideoIndex === 0)));
      
      if (canNavigate) {
        if (isLeftSwipe) {
          // Next story type
          const nextIndex = (currentStoryIndex + 1) % stories.length;
          const nextStory = stories[nextIndex];
          setCurrentStoryIndex(nextIndex);
          setModalContent(nextStory);

          // Mark first content item of next story as viewed
          if (nextStory.data && nextStory.data.length > 0) {
            const firstItem = nextStory.data[0];
            let contentId, storageKey;

            if (nextStory.type === 'insights') {
              contentId = `insight-${firstItem.lecture_id}-0`;
              storageKey = 'insights';
            } else if (nextStory.type === 'announcement') {
              contentId = firstItem.id;
              storageKey = 'announcements';
            } else if (nextStory.type === 'shorts') {
              contentId = firstItem.id || firstItem.videoId;
              storageKey = 'shorts';
            } else if (nextStory.type === 'videos') {
              contentId = firstItem.id || firstItem.videoId;
              storageKey = 'videos';
            } else if (nextStory.type === 'live-videos') {
              contentId = firstItem.id || firstItem.videoId;
              storageKey = 'liveVideos';
            }

            if (contentId && storageKey) {
              markContentAsViewed(storageKey, contentId);
            }
          }

          // Reset indices when switching stories
          setCurrentAnnouncementIndex(0);
          setCurrentVideoIndex(0);
          setCurrentShortIndex(0);
          setCurrentInsightIndex(0);
        }
        if (isRightSwipe) {
          // Previous story type
          const prevIndex = currentStoryIndex === 0
            ? stories.length - 1
            : currentStoryIndex - 1;
          const prevStory = stories[prevIndex];
          setCurrentStoryIndex(prevIndex);
          setModalContent(prevStory);

          // Mark first content item of previous story as viewed
          if (prevStory.data && prevStory.data.length > 0) {
            const firstItem = prevStory.data[0];
            let contentId, storageKey;

            if (prevStory.type === 'insights') {
              contentId = `insight-${firstItem.lecture_id}-0`;
              storageKey = 'insights';
            } else if (prevStory.type === 'announcement') {
              contentId = firstItem.id;
              storageKey = 'announcements';
            } else if (prevStory.type === 'shorts') {
              contentId = firstItem.id || firstItem.videoId;
              storageKey = 'shorts';
            } else if (prevStory.type === 'videos') {
              contentId = firstItem.id || firstItem.videoId;
              storageKey = 'videos';
            } else if (prevStory.type === 'live-videos') {
              contentId = firstItem.id || firstItem.videoId;
              storageKey = 'liveVideos';
            }

            if (contentId && storageKey) {
              markContentAsViewed(storageKey, contentId);
            }
          }

          // Reset indices when switching stories
          setCurrentAnnouncementIndex(0);
          setCurrentVideoIndex(0);
          setCurrentShortIndex(0);
          setCurrentInsightIndex(0);
        }
      }
    }
  };

  // Format announcement content with proper paragraph and line break handling
  const formatAnnouncementContent = (content) => {
    if (!content) return '';
    
    // First, normalize line breaks
    const normalized = content
      .replace(/\r\n/g, '\n')  // Windows line breaks to Unix
      .replace(/\\n/g, '\n')   // Escaped line breaks to actual line breaks
      .trim();
    
    // Split into paragraphs on double line breaks
    const paragraphs = normalized.split(/\n\n+/);
    
    // Format each paragraph with proper spacing
    return paragraphs
      .filter(para => para.trim())  // Remove empty paragraphs
      .map(para => {
        // Replace single line breaks with <br /> within paragraphs
        const formatted = para.trim().replace(/\n/g, '<br />');
        return `<p style="margin: 0 0 16px 0; line-height: 1.7; color: #374151">${formatted}</p>`;
      })
      .join('');
  };

  // Handle story click
  const handleStoryClick = (story) => {
    // Show modal immediately for instant visual feedback
    flushSync(() => {
      setShowModal(true);
    });
    
    // Trigger entrance animation on next frame
    requestAnimationFrame(() => {
      setIsModalEntering(true);
    });
    
    // Defer heavy state updates
    startTransition(() => {
      setActiveStory(story);
      setModalContent(story);
      
      // Reset indices when opening stories
      if (story.type === 'announcement') {
        setCurrentAnnouncementIndex(0);
      }
      if (story.type === 'videos') {
        setCurrentVideoIndex(0);
      }
      if (story.type === 'shorts') {
        setCurrentShortIndex(0);
        setShortsAutoPlay(true);
      }
      if (story.type === 'lecture-clips') {
        setCurrentClipIndex(0);
      }
      if (story.type === 'insights') {
        setCurrentInsightIndex(0);
      }
      
      // Set the current story index
      const index = stories.findIndex(s => s.id === story.id);
      setCurrentStoryIndex(index >= 0 ? index : 0);
      
      if (onStoryClick) {
        onStoryClick(story.type, story.data);
      }
    });
    
    // Move localStorage work off critical path - mark first content item as viewed
    requestAnimationFrame(() => {
      if (story.data && story.data.length > 0) {
        const firstItem = story.data[0];
        let contentId;
        let storageKey;

        // Get content ID based on story type
        if (story.type === 'insights') {
          contentId = `insight-${firstItem.lecture_id}-0`;
          storageKey = 'insights';
        } else if (story.type === 'announcement') {
          contentId = firstItem.id;
          storageKey = 'announcements';
        } else if (story.type === 'shorts') {
          contentId = firstItem.id || firstItem.videoId;
          storageKey = 'shorts';
        } else if (story.type === 'videos') {
          contentId = firstItem.id || firstItem.videoId;
          storageKey = 'videos';
        } else if (story.type === 'live-videos') {
          contentId = firstItem.id || firstItem.videoId;
          storageKey = 'liveVideos';
        }

        if (contentId && storageKey) {
          markContentAsViewed(storageKey, contentId);
        }
      }
    });
  };

  // Handle share Majlis Insight
  // Decode HTML entities to clean text
  const decodeHtmlEntities = (text) => {
    const entities = {
      '&nbsp;': ' ',
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#39;': "'",
      '&apos;': "'",
      '&ndash;': '–',
      '&mdash;': '—',
      '&hellip;': '…',
      '&copy;': '©',
      '&reg;': '®',
      '&trade;': '™',
      '&ldquo;': '"',
      '&rdquo;': '"',
      '&lsquo;': "'",
      '&rsquo;': "'",
      '&bull;': '•',
      '&middot;': '·'
    };
    
    // Decode named entities
    let decodedText = text;
    for (const [entity, char] of Object.entries(entities)) {
      decodedText = decodedText.replace(new RegExp(entity, 'gi'), char);
    }
    
    // Decode numeric entities (e.g., &#160; for nbsp)
    decodedText = decodedText.replace(/&#(\d+);/g, (match, code) => {
      return String.fromCharCode(parseInt(code, 10));
    });
    
    // Decode hex entities (e.g., &#xA0; for nbsp)
    decodedText = decodedText.replace(/&#x([0-9A-F]+);/gi, (match, code) => {
      return String.fromCharCode(parseInt(code, 16));
    });
    
    return decodedText;
  };
  
  // Format content for sharing with proper spacing and line breaks
  const formatShareText = (content, maxLength = 300) => {
    if (!content) return '';
    
    // First, convert common HTML elements to preserve structure
    let formattedText = content
      // Convert paragraph tags to double line breaks
      .replace(/<\/p>\s*<p>/gi, '\n\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p>/gi, '')
      // Convert br tags to single line breaks
      .replace(/<br\s*\/?>/gi, '\n')
      // Convert list items to bullet points
      .replace(/<li>/gi, '• ')
      .replace(/<\/li>/gi, '\n')
      // Remove remaining HTML tags
      .replace(/<[^>]*>/g, '');
    
    // Decode HTML entities after removing tags
    formattedText = decodeHtmlEntities(formattedText);
    
    // Clean up whitespace
    formattedText = formattedText
      .replace(/\n{3,}/g, '\n\n')  // Replace multiple line breaks with double
      .replace(/^\s+|\s+$/g, '')    // Trim start and end
      .replace(/[ \t]+/g, ' ');     // Replace multiple spaces with single
    
    // Truncate if needed
    if (formattedText.length > maxLength) {
      formattedText = formattedText.substring(0, maxLength) + '...';
    }
    
    return formattedText;
  };
  
  // Handle share for announcements - enhanced version matching announcements page
  const handleShareAnnouncement = async (announcement, index) => {
    try {
      // Use direct function URL with shortened ID (first 8 characters)
      const shortId = announcement.id.substring(0, 8);
      const shareUrl = `${window.location.origin}/.netlify/functions/share-announcement?id=${shortId}`;
      const shareTitle = announcement.title;
      
      // Format the share text with friendly message
      const formattedBody = formatShareText(announcement.content);
      const shareText = `${shareTitle}\n\n${formattedBody}\n\n📍 For more details:`;
      
      // Helper function to slugify title for filename
      const slugify = (s) =>
        (s || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      
      // Function to get image file for sharing
      const getShareImageFile = async () => {
        if (!announcement?.image_url) return null;
        try {
          const imgUrl = new URL(announcement.image_url, window.location.origin);
          const sameOrigin = imgUrl.origin === window.location.origin;
          const proxyUrl = sameOrigin
            ? imgUrl.toString()
            : `${window.location.origin}/.netlify/functions/image-proxy?url=${encodeURIComponent(
                imgUrl.toString()
              )}&filename=${encodeURIComponent(slugify(announcement.title) || 'announcement')}`;

          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 8000);
          const res = await fetch(proxyUrl, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
            signal: controller.signal,
          });
          clearTimeout(timeout);
          if (!res.ok) return null;

          const blob = await res.blob();
          const type = blob.type || 'image/jpeg';
          const ext = type.includes('png')
            ? 'png'
            : type.includes('webp')
            ? 'webp'
            : type.includes('gif')
            ? 'gif'
            : 'jpg';
          const filename = `${slugify(announcement.title) || 'announcement'}.${ext}`;
          return new File([blob], filename, { type });
        } catch (e) {
          console.warn('Share image fetch failed, falling back to URL-only share:', e);
          return null;
        }
      };
      
      // Check if Web Share API is available
      if (navigator.share) {
        const shareData = {
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        };
        
        // Try to include image if available and supported
        if (navigator.canShare && announcement?.image_url) {
          const imageFile = await getShareImageFile();
          if (imageFile && navigator.canShare({ files: [imageFile] })) {
            shareData.files = [imageFile];
          }
        }
        
        await navigator.share(shareData);
      } else {
        // Desktop fallback: copy formatted text and URL to clipboard
        const fullShareText = `${shareTitle}\n\n${formattedBody}\n\n📍 For more details:\n${shareUrl}`;
        await navigator.clipboard.writeText(fullShareText);
        
        // Show toast notification instead of alert
        showShareToast('Announcement details copied to clipboard!');
      }
    } catch (err) {
      console.error('Error sharing announcement:', err);
      if (err.name !== 'AbortError') {
        // Fallback: just copy the URL
        try {
          const url = `${window.location.origin}/community/announcements#announcement-${announcement.id}`;
          await navigator.clipboard.writeText(url);
          showShareToast('Link copied to clipboard!');
        } catch (clipErr) {
          console.error('Could not copy to clipboard:', clipErr);
          showShareToast('Unable to share. Please try again.', true);
        }
      }
    }
  };
  
  // Helper function to show toast notification
  const showShareToast = (message, isError = false) => {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      background-color: ${isError ? '#ef4444' : '#10b981'};
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 14px;
      z-index: 999999;
      animation: slideUp 0.3s ease-out;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    // Add animation styles if not already present
    if (!document.getElementById('share-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'share-toast-styles';
      style.textContent = `
        @keyframes slideUp {
          from { transform: translateX(-50%) translateY(20px); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Remove toast after 3 seconds
    setTimeout(() => {
      toast.style.animation = 'slideUp 0.3s ease-out reverse';
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  };

  const handleShareInsight = async (insight, index) => {
    const insightCardRef = document.getElementById(`insight-card-${index}`);
    
    if (!insightCardRef) {
      console.error('Insight card element not found');
      return;
    }

    try {
      // Hide share button temporarily
      const shareButton = insightCardRef.querySelector('[data-share-button]');
      if (shareButton) {
        shareButton.style.display = 'none';
      }

      // Capture the insight card as canvas
      const canvas = await html2canvas(insightCardRef, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true
      });

      // Show share button again
      if (shareButton) {
        shareButton.style.display = 'flex';
      }

      // Convert canvas to blob
      const blob = await new Promise(resolve => 
        canvas.toBlob(resolve, 'image/png')
      );

      // Create file from blob
      const fileName = `majlis-insight-${new Date().toISOString().split('T')[0]}.png`;
      const file = blob ? new File([blob], fileName, { type: 'image/png' }) : null;

      // Share data
      const shareData = {
        title: 'Majlis Insight',
        text: `Majlis Insight from ${insight.lecture_title || 'today\'s lecture'}${insight.speaker ? ' by ' + insight.speaker : ''}`,
        url: `${window.location.origin}?insight=majlis-${index}-${Date.now()}`
      };

      // Try to share with image on mobile
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          ...shareData,
          files: [file]
        });
      } else if (navigator.share) {
        // Share without image
        await navigator.share(shareData);
      } else if (file) {
        // Desktop: download image and copy link
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        await navigator.clipboard.writeText(shareData.url);
        alert('Insight image downloaded and link copied to clipboard!');
      } else {
        // Just copy link
        await navigator.clipboard.writeText(shareData.url);
        alert('Insight link copied to clipboard!');
      }
    } catch (err) {
      console.error('Error sharing insight:', err);
      // Fallback to copy link
      try {
        const url = `${window.location.origin}?insight=majlis-${index}-${Date.now()}`;
        await navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
      } catch (clipErr) {
        console.error('Could not copy to clipboard:', clipErr);
      }
    }
  };

  // Close modal
  const closeModal = () => {
    setIsModalEntering(false);
    setShowModal(false);
    setModalContent(null);
    setActiveStory(null);
    setVideoAutoPlay(false);
    setVideoProgress(0);
    if (videoTimerRef.current) {
      clearInterval(videoTimerRef.current);
      videoTimerRef.current = null;
    }
  };
  
  // Auto-advance videos with timer
  useEffect(() => {
    if (modalContent?.type === 'videos' && videoAutoPlay && Array.isArray(modalContent?.data)) {
      const startTime = Date.now();
      
      // Update progress bar
      videoTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = (elapsed / PREVIEW_DURATION) * 100;
        
        if (progress >= 100) {
          // Move to next video
          const nextIndex = currentVideoIndex + 1;
          if (nextIndex < (modalContent?.data?.length || 0)) {
            setCurrentVideoIndex(nextIndex);
            setVideoProgress(0);
          } else {
            // End of videos, stop autoplay
            setVideoAutoPlay(false);
            setVideoProgress(0);
          }
        } else {
          setVideoProgress(progress);
        }
      }, 100);
      
      return () => {
        if (videoTimerRef.current) {
          clearInterval(videoTimerRef.current);
          videoTimerRef.current = null;
        }
      };
    }
  }, [currentVideoIndex, videoAutoPlay, modalContent]);
  
  // Auto-advance shorts with timer
  useEffect(() => {
    if (modalContent?.type === 'shorts' && shortsAutoPlay && Array.isArray(modalContent?.data)) {
      const startTime = Date.now();
      
      // Update progress bar
      shortsTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = (elapsed / PREVIEW_DURATION) * 100;
        
        if (progress >= 100) {
          // Move to next short
          const nextIndex = currentShortIndex + 1;
          if (nextIndex < (modalContent?.data?.length || 0)) {
            setCurrentShortIndex(nextIndex);
            setShortsProgress(0);
          } else {
            // End of shorts, stop autoplay
            setShortsAutoPlay(false);
            setShortsProgress(0);
          }
        } else {
          setShortsProgress(progress);
        }
      }, 100);
      
      return () => {
        if (shortsTimerRef.current) {
          clearInterval(shortsTimerRef.current);
          shortsTimerRef.current = null;
        }
      };
    }
  }, [currentShortIndex, shortsAutoPlay, modalContent]);

  // Styles
  const styles = {
    container: {
      backgroundColor: theme?.colors?.background || '#f8f9fa',
      padding: '12px 0 8px',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    },
    scrollContainer: {
      display: 'flex',
      gap: '16px',
      padding: '0 16px',
      minWidth: 'min-content'
    },
    storyItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      cursor: 'pointer',
      minWidth: '68px'
    },
    storyCircle: {
      width: '58px',
      height: '58px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      background: 'linear-gradient(145deg, #f8f9fa, #e9ecef)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
    },
    storyCircleActive: {
      padding: '3px',
      background: `linear-gradient(45deg, ${theme?.colors?.primary || '#0f172a'}, ${getLighterColor(theme?.colors?.primary) || '#1e293b'})`,
      animation: 'rotate 2s linear infinite'
    },
    storyCircleLive: {
      padding: '3px',
      background: 'linear-gradient(45deg, #dc2626, #ef4444)',
      animation: 'pulse 1.5s infinite'
    },
    storyCircleNew: {
      padding: '3px',
      background: `linear-gradient(45deg, ${theme?.colors?.primary || '#0f172a'}, ${getLighterColor(theme?.colors?.primary) || '#1e293b'})`
    },
    storyCircleUnread: {
      padding: '3px'
      // Background will be set dynamically with CSS variables
    },
    storyCircleRead: {
      padding: '2px',
      background: '#d1d5db'  // Gray for read stories
    },
    storyInner: {
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    storyLabel: {
      fontSize: '11px',
      fontWeight: '500',
      color: '#4b5563',
      textAlign: 'center',
      maxWidth: '72px',
      lineHeight: '1.3',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden'
    },
    liveIndicator: {
      position: 'absolute',
      bottom: '-2px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: '#dc2626',
      color: 'white',
      padding: '2px 6px',
      borderRadius: '10px',
      fontSize: '9px',
      fontWeight: '600',
      zIndex: 1
    },
    newBadge: {
      position: 'absolute',
      top: '-4px',
      right: '-4px',
      backgroundColor: theme?.colors?.primary || '#0f172a',
      color: 'white',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '10px',
      fontWeight: '600',
      border: '2px solid white',
      zIndex: 1
    }
  };

  // SVG icons for different story types — clean, minimal, consistent
  const iconColor = theme?.colors?.primary || '#1e293b';
  const getStoryIcon = (type) => {
    const size = 22;
    switch(type) {
      case 'announcement':
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
      case 'insights':
      case 'insight':
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/></svg>;
      case 'live':
      case 'live-videos':
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" fill="#dc2626" opacity="0.15"/><circle cx="12" cy="12" r="2" fill="#dc2626"/><path d="M4.93 4.93a10 10 0 0 0 0 14.14"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>;
      case 'video':
      case 'videos':
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" fill={iconColor} opacity="0.1"/><polygon points="5 3 19 12 5 21 5 3"/></svg>;
      case 'shorts':
      case 'short':
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="2" width="10" height="20" rx="3"/><line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="2"/></svg>;
      case 'lecture-clips':
        // Film clapperboard icon — distinct from shorts (phone) and videos (play)
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="8" width="20" height="14" rx="2"/><path d="M2 8l4-6h4l-4 6"/><path d="M10 2h4l-4 6"/><path d="M14 2h4l-4 6"/><polygon points="10 12 10 20 16 16 10 12" fill={iconColor} opacity="0.15"/><polygon points="10 12 10 20 16 16 10 12"/></svg>;
      case 'lecture':
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>;
      default:
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
    }
  };

  // Don't render if no stories
  if (stories.length === 0) {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.scrollContainer}>
        {stories.map((story) => (
          <div
            key={story.id}
            style={styles.storyItem}
            onClick={() => handleStoryClick(story)}
          >
            <div style={{
              ...styles.storyCircle,
              ...(story.isLive && styles.storyCircleLive),
              ...(!story.isLive && !isAllContentViewed(story.type, story.data) && {
                ...styles.storyCircleUnread,
                background: `linear-gradient(45deg, ${story.color || '#3b82f6'}, ${getLighterColor(story.color) || '#60a5fa'})`
              }),
              ...(!story.isLive && isAllContentViewed(story.type, story.data) && styles.storyCircleRead),
              ...(activeStory?.id === story.id && styles.storyCircleActive)
            }}>
              <div style={styles.storyInner}>
                {getStoryIcon(story.type)}
              </div>
              {story.isLive && (
                <div style={styles.liveIndicator}>LIVE</div>
              )}
              {story.isNew && (
                <div style={styles.newBadge}>!</div>
              )}
            </div>
            <span style={styles.storyLabel}>{story.label}</span>
          </div>
        ))}
      </div>

      {/* Story Modal */}
      {showModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 2147483647, // above GlobalVideoOverlay (2147483000)
            isolation: 'isolate',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            opacity: isModalEntering ? 1 : 0,
            transition: 'opacity 200ms ease-out',
            willChange: 'opacity'
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative',
              animation: 'contentFadeIn 0.15s ease-out',
              transform: 'translateZ(0)', // GPU acceleration
              willChange: 'opacity'
            }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#6b7280',
                zIndex: 1
              }}
            >
              ×
            </button>

            {/* Story type indicator */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: modalContent?.color ? modalContent.color + '10' : 'transparent',
              borderRadius: '12px 12px 0 0'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '24px' }}>
                  {modalContent && getStoryIcon(modalContent?.type)}
                </span>
                <div>
                  <h3 style={{
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    {modalContent?.type === 'announcement' && 'Announcement'}
                    {modalContent?.type === 'videos' && 'Latest Videos'}
                    {modalContent?.type === 'live-videos' && 'Live Videos'}
                    {modalContent?.type === 'lecture-clips' && 'Lecture Clips'}
                    {modalContent?.type === 'shorts' && 'Latest Shorts'}
                    {modalContent?.type === 'insights' && 'Majlis Insights'}
                    {modalContent?.type === 'insight' && "Today's Insight"}
                    {modalContent?.type === 'live' && 'Live Event'}
                    {modalContent?.type === 'video' && 'Latest Video'}
                    {modalContent?.type === 'short' && 'Quick Clip'}
                    {modalContent?.type === 'lecture' && 'Latest Lecture'}
                  </h3>
                  {modalContent?.isLive && (
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      marginTop: '4px'
                    }}>
                      LIVE NOW
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Swipe Progress Indicators */}
            {(modalContent?.type === 'announcement' || modalContent?.type === 'videos' || modalContent?.type === 'live-videos' || modalContent?.type === 'shorts' || modalContent?.type === 'lecture-clips' || modalContent?.type === 'insights') && Array.isArray(modalContent?.data) ? (
              // For announcements, show progress through announcements
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <span style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  fontWeight: '500'
                }}>
                  {modalContent?.type === 'insights'
                    ? `${currentInsightIndex + 1} of ${modalContent?.data?.length || 0}`
                    : modalContent?.type === 'announcement'
                    ? `${currentAnnouncementIndex + 1} of ${modalContent?.data?.length || 0}`
                    : modalContent?.type === 'shorts'
                    ? `${currentShortIndex + 1} of ${modalContent?.data?.length || 0}`
                    : modalContent?.type === 'lecture-clips'
                    ? `${currentClipIndex + 1} of ${modalContent?.data?.length || 0}`
                    : `${currentVideoIndex + 1} of ${modalContent?.data?.length || 0}`
                  }
                </span>
                <div style={{
                  display: 'flex',
                  gap: '6px'
                }}>
                  {modalContent?.data?.map((_, index) => (
                    <div
                      key={index}
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: modalContent?.type === 'insights'
                          ? (index === currentInsightIndex ? (theme?.colors?.primary || '#0f172a') : '#e5e7eb')
                          : modalContent?.type === 'announcement'
                          ? (index === currentAnnouncementIndex ? (modalContent?.color || '#e5e7eb') : '#e5e7eb')
                          : modalContent?.type === 'shorts'
                          ? (index === currentShortIndex ? (modalContent?.color || '#e5e7eb') : '#e5e7eb')
                          : modalContent?.type === 'lecture-clips'
                          ? (index === currentClipIndex ? (modalContent?.color || '#e5e7eb') : '#e5e7eb')
                          : (index === currentVideoIndex ? (modalContent?.color || '#e5e7eb') : '#e5e7eb'),
                        transition: 'all 0.3s',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (modalContent?.type === 'insights') {
                          setCurrentInsightIndex(index);
                          const insight = modalContent.data[index];
                          const insightId = `insight-${insight.lecture_id}-${index}`;
                          markContentAsViewed('insights', insightId);
                        } else if (modalContent?.type === 'announcement') {
                          setCurrentAnnouncementIndex(index);
                          const announcement = modalContent.data[index];
                          markContentAsViewed('announcements', announcement.id);
                        } else if (modalContent?.type === 'shorts') {
                          setCurrentShortIndex(index);
                          const short = modalContent.data[index];
                          markContentAsViewed('shorts', short.id || short.videoId);
                        } else if (modalContent?.type === 'lecture-clips') {
                          setCurrentClipIndex(index);
                        } else if (modalContent?.type === 'videos') {
                          setCurrentVideoIndex(index);
                          const video = modalContent.data[index];
                          markContentAsViewed('videos', video.id || video.videoId);
                        } else if (modalContent?.type === 'live-videos') {
                          setCurrentVideoIndex(index);
                          const video = modalContent.data[index];
                          markContentAsViewed('liveVideos', video.id || video.videoId);
                        }
                      }}
                    />
                  ))}
                  </div>
                
                {/* Share Button for Insights and Announcements */}
                {(modalContent?.type === 'insights' || modalContent?.type === 'announcement') && (
                  <button
                    onClick={() => {
                      if (modalContent?.type === 'insights') {
                        handleShareInsight(modalContent?.data?.[currentInsightIndex], currentInsightIndex);
                      } else if (modalContent?.type === 'announcement') {
                        const currentAnnouncement = Array.isArray(modalContent?.data) 
                          ? modalContent?.data?.[currentAnnouncementIndex] 
                          : modalContent?.data;
                        handleShareAnnouncement(currentAnnouncement, currentAnnouncementIndex);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 14px',
                      backgroundColor: theme?.colors?.primary || '#0f172a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme?.colors?.primary ? `${theme.colors.primary}dd` : '#1e293b';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = theme?.colors?.primary || '#0f172a';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <ShareIcon style={{ fontSize: '16px' }} />
                    <span>Share</span>
                  </button>
                )}
              </div>
            ) : stories.length > 1 && (
              // For other stories, show navigation between story types
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '6px',
                padding: '12px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                {stories.map((_, index) => (
                  <div
                    key={index}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: index === currentStoryIndex ? (modalContent?.color || theme?.colors?.primary || '#0f172a') : '#e5e7eb',
                      transition: 'all 0.3s',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const selectedStory = stories[index];
                      setCurrentStoryIndex(index);
                      setModalContent(selectedStory);

                      // Mark first content item of selected story as viewed
                      if (selectedStory.data && selectedStory.data.length > 0) {
                        const firstItem = selectedStory.data[0];
                        let contentId, storageKey;

                        if (selectedStory.type === 'insights') {
                          contentId = `insight-${firstItem.lecture_id}-0`;
                          storageKey = 'insights';
                        } else if (selectedStory.type === 'announcement') {
                          contentId = firstItem.id;
                          storageKey = 'announcements';
                        } else if (selectedStory.type === 'shorts') {
                          contentId = firstItem.id || firstItem.videoId;
                          storageKey = 'shorts';
                        } else if (selectedStory.type === 'videos') {
                          contentId = firstItem.id || firstItem.videoId;
                          storageKey = 'videos';
                        } else if (selectedStory.type === 'live-videos') {
                          contentId = firstItem.id || firstItem.videoId;
                          storageKey = 'liveVideos';
                        }

                        if (contentId && storageKey) {
                          markContentAsViewed(storageKey, contentId);
                        }
                      }

                      // Reset indices when switching stories
                      setCurrentAnnouncementIndex(0);
                      setCurrentVideoIndex(0);
                      setCurrentShortIndex(0);
                      setCurrentInsightIndex(0);
                    }}
                  />
                ))}
              </div>
            )}
            
            {/* Content - Scrollable if needed */}
            <div style={{ 
              padding: (modalContent?.type === 'shorts' || modalContent?.type === 'lecture-clips') ? '16px 20px' : '20px',
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden'
            }}>
              {/* Announcement content */}
              {modalContent?.type === 'announcement' && modalContent?.data && (
                <div style={{ position: 'relative' }}>
                  {/* Get current announcement from array */}
                  {(() => {
                    const currentAnnouncement = Array.isArray(modalContent?.data) 
                      ? modalContent?.data?.[currentAnnouncementIndex] 
                      : modalContent?.data;
                    
                    return (
                      <>
                        {/* Flyer Image or PDF - Show first for visual impact */}
                        {currentAnnouncement.image_url && (
                    <div style={{
                      marginBottom: '20px',
                      marginLeft: '-20px',
                      marginRight: '-20px',
                      marginTop: '-20px',
                      borderRadius: '0 0 8px 8px',
                      overflow: 'hidden',
                      backgroundColor: '#f3f4f6',
                      maxHeight: '300px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                          {isPdfUrl(currentAnnouncement.image_url) ? (
                            // PDF Display - Compact preview with button
                            <div style={{
                              width: '100%',
                              height: '150px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f9fafb',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <svg 
                                width="48" 
                                height="48" 
                                viewBox="0 0 24 24" 
                                fill="#dc2626"
                                style={{ marginBottom: '10px' }}
                              >
                                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"/>
                                <path d="M8 15h8v2H8zm0-3h8v2H8z"/>
                              </svg>
                              <p style={{
                                fontSize: '14px',
                                color: '#374151',
                                marginBottom: '8px',
                                fontWeight: '500'
                              }}>PDF Document</p>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('[StoriesBar] PDF button clicked, URL:', currentAnnouncement.image_url);
                                  console.log('[StoriesBar] Current announcement:', currentAnnouncement);
                                  if (currentAnnouncement.image_url) {
                                    setPdfUrl(currentAnnouncement.image_url);
                                    setPdfAnnouncement(currentAnnouncement);
                                    console.log('[StoriesBar] PDF viewer should now open');
                                  } else {
                                    console.error('[StoriesBar] No PDF URL available');
                                  }
                                }}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#dc2626',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#b91c1c';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dc2626';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
                                </svg>
                                View PDF
                              </button>
                            </div>
                          ) : (
                            // Image Display
                            <img
                              src={currentAnnouncement.image_url}
                              alt={currentAnnouncement.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                maxHeight: '300px',
                                objectFit: 'cover',
                                cursor: 'pointer'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedImage(currentAnnouncement.image_url);
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.style.display = 'none';
                              }}
                            />
                          )}
                        </div>
                        )}
                        
                        {/* Title */}
                        <h4 style={{
                          fontSize: '20px',
                          fontWeight: '600',
                          marginBottom: '8px',
                          color: '#1f2937',
                          lineHeight: '1.3'
                        }}>
                          {currentAnnouncement.title}
                        </h4>
                        
                        {/* Date */}
                        <p style={{
                          fontSize: '14px',
                          color: '#6b7280',
                          marginBottom: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>📅</span>
                          {new Date(currentAnnouncement.date || currentAnnouncement.created_at).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                        
                        {/* Description/Content */}
                        <div 
                          style={{
                            fontSize: '15px',
                            color: '#374151'
                          }}
                          dangerouslySetInnerHTML={{ 
                            __html: formatAnnouncementContent(
                              currentAnnouncement.description || currentAnnouncement.content || ''
                            )
                          }}
                        />
                        {currentAnnouncement.link && (
                          <a
                            href={currentAnnouncement.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-block',
                              marginTop: '16px',
                              padding: '8px 16px',
                              backgroundColor: theme?.colors?.primary || '#0f172a',
                              color: 'white',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                          >
                            Learn More →
                          </a>
                        )}
                        
                        {/* Share in Blessings Donation Section - Only show for upcoming events */}
                        {(() => {
                          // Check if donation button should be shown
                          const sponsorEnabled = currentAnnouncement.sponsor_enabled !== false;
                          const isTodayOrUpcoming = isEventTodayOrUpcoming(currentAnnouncement);
                          const shouldShowSponsor = sponsorEnabled && isTodayOrUpcoming;
                          
                          return shouldShowSponsor && (
                            <div style={{
                              marginTop: '24px',
                              paddingTop: '24px',
                              borderTop: '1px solid #e5e7eb',
                              textAlign: 'center'
                            }}>
                              <p style={{
                                fontSize: '15px',
                                color: '#6b7280',
                                marginBottom: '16px',
                                lineHeight: '1.5'
                              }}>
                                Support our upcoming programs and events
                              </p>
                              
                              {/* SponsorshipFlow Component with Share in Blessings Button */}
                              <SponsorshipFlow
                                announcement={{
                                  ...currentAnnouncement,
                                  sponsor_enabled: true,
                                  suggested_amounts: DONATION_AMOUNTS.default
                                }}
                                center={{
                                  id: config?.center_id,
                                  name: config?.name,
                                  code: config?.code || 'DEZ'
                                }}
                                centerConfig={config}
                                isInModal={true}
                              />
                            </div>
                          );
                        })()}
                        
                        {/* View All Announcements Link */}
                        <div style={{
                          marginTop: '20px',
                          paddingTop: '20px',
                          borderTop: '1px solid #e5e7eb',
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          <a
                            href="/community/announcements"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 20px',
                              backgroundColor: '#f3f4f6',
                              color: '#374151',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              fontSize: '14px',
                              fontWeight: '500',
                              transition: 'all 0.2s',
                              width: '100%',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#e5e7eb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }}
                          >
                            View All Announcements →
                          </a>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Videos content */}
              {(modalContent?.type === 'videos' || modalContent?.type === 'live-videos') && modalContent?.data && (
                <div>
                  {/* Get current video from array */}
                  {(() => {
                    const currentVideo = Array.isArray(modalContent?.data) 
                      ? modalContent?.data?.[currentVideoIndex] 
                      : modalContent?.data;
                    
                    return (
                      <>
                        {/* Video Preview with Autoplay Option */}
                        <div style={{
                          marginBottom: '16px',
                          marginLeft: '-20px',
                          marginRight: '-20px',
                          marginTop: '-20px',
                          borderRadius: '0 0 8px 8px',
                          overflow: 'hidden',
                          backgroundColor: '#000',
                          aspectRatio: '16/9',
                          position: 'relative'
                        }}>
                          {videoAutoPlay && (currentVideo.youtube_video_id || currentVideo.id) ? (
                            // Embedded YouTube player with autoplay
                            <iframe
                              src={`https://www.youtube.com/embed/${currentVideo.youtube_video_id || currentVideo.id}?autoplay=1&mute=0&controls=1&modestbranding=1&rel=0&enablejsapi=1`}
                              style={{
                                width: '100%',
                                height: '100%',
                                border: 'none'
                              }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title={currentVideo.title}
                            />
                          ) : currentVideo.thumbnail ? (
                            <img
                              src={currentVideo.thumbnail}
                              alt={currentVideo.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (currentVideo.youtube_video_id || currentVideo.id) ? (
                            <img
                              src={`https://img.youtube.com/vi/${currentVideo.youtube_video_id || currentVideo.id}/maxresdefault.jpg`}
                              alt={currentVideo.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#1f2937'
                            }}>
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                                <path d="M10 16.5l6-4.5-6-4.5v9z"/>
                              </svg>
                            </div>
                          )}
                          
                          {/* Play/Pause overlay */}
                          {!videoAutoPlay && (
                            <div 
                              onClick={() => setVideoAutoPlay(true)}
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                background: 'rgba(0, 0, 0, 0.7)',
                                borderRadius: '50%',
                                width: '60px',
                                height: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer'
                              }}>
                              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          )}
                          
                          {/* Autoplay Progress Bar */}
                          {videoAutoPlay && (
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${videoProgress}%`,
                                backgroundColor: '#ef4444',
                                transition: 'width 0.1s linear'
                              }} />
                            </div>
                          )}
                        </div>
                        
                        {/* Video Title */}
                        <h4 style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          marginBottom: '8px',
                          color: '#1f2937',
                          lineHeight: '1.3'
                        }}>
                          {currentVideo.title}
                        </h4>
                        
                        {/* Speaker & Duration */}
                        {(currentVideo.speakerName || currentVideo.duration) && (
                          <p style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            marginBottom: '12px'
                          }}>
                            {currentVideo.speakerName && <span>{currentVideo.speakerName}</span>}
                            {currentVideo.speakerName && currentVideo.duration && ' • '}
                            {currentVideo.duration && (
                              <span>
                                {typeof currentVideo.duration === 'string' 
                                  ? currentVideo.duration
                                  : `${Math.round(currentVideo.duration / 60)} min`
                                }
                              </span>
                            )}
                          </p>
                        )}
                        
                        {/* Description */}
                        {currentVideo.description && (
                          <p style={{
                            fontSize: '14px',
                            color: '#4b5563',
                            lineHeight: '1.5',
                            marginBottom: '16px'
                          }}>
                            {currentVideo.description}
                          </p>
                        )}
                        
                        {/* Action Buttons */}
                        <div style={{
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'center'
                        }}>
                          {/* Watch Full Video Button */}
                          <a
                            href={currentVideo.url || `/videos/${currentVideo.id}`}
                            target={currentVideo.url ? '_blank' : '_self'}
                            rel={currentVideo.url ? 'noreferrer' : undefined}
                            onClick={() => setShowModal(false)}
                            style={{
                              flex: 1,
                              display: 'inline-block',
                              padding: '10px 20px',
                              backgroundColor: theme?.colors?.primary || '#0f172a',
                              color: 'white',
                              borderRadius: '8px',
                              textDecoration: 'none',
                              fontWeight: '500',
                              fontSize: '14px',
                              textAlign: 'center'
                            }}
                          >
                            Watch Full Video
                          </a>
                          
                          {/* Preview Toggle Button */}
                          {(currentVideo.youtube_video_id || currentVideo.id) && (
                            <button
                              onClick={() => setVideoAutoPlay(!videoAutoPlay)}
                              style={{
                                padding: '10px 16px',
                                backgroundColor: videoAutoPlay ? '#ef4444' : (theme?.colors?.primary || '#0f172a'),
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              {videoAutoPlay ? (
                                <>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                    <path d="M6 6h4v12H6zM14 6h4v12h-4z"/>
                                  </svg>
                                  Stop
                                </>
                              ) : (
                                <>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                  Preview
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Shorts content - Elegant vertical design */}
              {modalContent?.type === 'shorts' && modalContent?.data && (
                <div>
                  {/* Get current short from array */}
                  {(() => {
                    const currentShort = Array.isArray(modalContent?.data) 
                      ? modalContent?.data?.[currentShortIndex] 
                      : modalContent?.data;
                    
                    // Extract video ID from shortUrl if needed
                    const getVideoId = (short) => {
                      if (short.videoId) return short.videoId;
                      if (short.youtube_video_id) return short.youtube_video_id;
                      if (short.shortUrl) {
                        const match = short.shortUrl.match(/(?:youtube\.com\/shorts\/|youtu\.be\/)([^#&?]*)/);
                        return match ? match[1] : null;
                      }
                      return null;
                    };
                    
                    const videoId = getVideoId(currentShort);
                    
                    return (
                      <>
                        {/* Short Preview with Vertical Aspect Ratio */}
                        <div style={{
                          marginBottom: '12px',
                          marginLeft: '-20px',
                          marginRight: '-20px',
                          marginTop: '-20px',
                          borderRadius: '0 0 12px 12px',
                          overflow: 'hidden',
                          backgroundColor: '#000',
                          position: 'relative',
                          background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)',
                          height: window.innerWidth <= 768 ? '320px' : '380px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {/* Inner container to maintain aspect ratio */}
                          <div style={{
                            width: '100%',
                            height: '100%',
                            maxWidth: window.innerWidth <= 768 ? '180px' : '213px', // 9:16 ratio
                            position: 'relative',
                            aspectRatio: '9/16'
                          }}>
                            {shortsAutoPlay && videoId ? (
                              // Embedded YouTube Shorts player
                              <iframe
                                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${shortsMuted ? 1 : 0}&controls=1&modestbranding=1&rel=0&loop=1&playlist=${videoId}`}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  border: 'none'
                                }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={currentShort.title}
                              />
                            ) : currentShort.thumbnail ? (
                              <img
                                src={currentShort.thumbnail}
                                alt={currentShort.title}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : videoId ? (
                              <img
                                src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                                alt={currentShort.title}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#1f2937'
                              }}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                                  <path d="M10 16.5l6-4.5-6-4.5v9z"/>
                                </svg>
                              </div>
                            )}
                            
                            {/* Play button overlay */}
                            {!shortsAutoPlay && videoId && (
                              <div
                                onClick={() => setShortsAutoPlay(true)}
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  background: 'rgba(0, 0, 0, 0.7)',
                                  borderRadius: '50%',
                                  width: '60px',
                                  height: '60px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  backdropFilter: 'blur(10px)',
                                  border: '2px solid rgba(255, 255, 255, 0.3)',
                                  zIndex: 2
                                }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                                  <path d="M8 5v14l11-7z"/>
                                </svg>
                              </div>
                            )}

                            {/* Volume Control Button - Instagram/TikTok style */}
                            {shortsAutoPlay && videoId && (
                              <div
                                onClick={() => setShortsMuted(!shortsMuted)}
                                style={{
                                  position: 'absolute',
                                  bottom: '20px',
                                  left: '-50px',
                                  background: 'rgba(0, 0, 0, 0.7)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '48px',
                                  height: '48px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  zIndex: 10,
                                  backdropFilter: 'blur(10px)',
                                  border: '2px solid rgba(255, 255, 255, 0.3)',
                                  transition: 'all 0.2s ease'
                                }}
                                aria-label={shortsMuted ? 'Unmute' : 'Mute'}
                              >
                                {shortsMuted ? (
                                  // Muted icon
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                                  </svg>
                                ) : (
                                  // Unmuted icon
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                  </svg>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Autoplay Progress Bar */}
                          {shortsAutoPlay && (
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '3px',
                              backgroundColor: 'rgba(255, 255, 255, 0.2)'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${shortsProgress}%`,
                                backgroundColor: '#8b5cf6',
                                transition: 'width 0.1s linear',
                                boxShadow: '0 0 8px rgba(139, 92, 246, 0.6)'
                              }} />
                            </div>
                          )}
                          
                          {/* Shorts badge */}
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            background: 'rgba(0, 0, 0, 0.6)',
                            backdropFilter: 'blur(10px)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M10 16.5l6-4.5-6-4.5v9z"/>
                            </svg>
                            SHORT
                          </div>
                        </div>
                        
                        {/* Short Title */}
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          marginBottom: '6px',
                          color: '#1f2937',
                          lineHeight: '1.2',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {currentShort.title || 'Untitled Short'}
                        </h4>
                        
                        {/* Speaker and Series - More compact */}
                        {(currentShort.speakerName || (currentShort.series && currentShort.series !== 'all')) && (
                          <p style={{
                            fontSize: '13px',
                            color: '#6b7280',
                            marginBottom: '10px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {currentShort.speakerName && <span>{currentShort.speakerName}</span>}
                            {currentShort.speakerName && currentShort.series && currentShort.series !== 'all' && ' • '}
                            {currentShort.series && currentShort.series !== 'all' && <span>{currentShort.series}</span>}
                          </p>
                        )}
                        
                        {/* Action Buttons - More compact */}
                        <div style={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'center'
                        }}>
                          {/* Watch Full Short Button */}
                          <a
                            href={`/shorts?v=${encodeURIComponent(currentShort.shortUrl || '')}`}
                            onClick={() => setShowModal(false)}
                            style={{
                              flex: 1,
                              display: 'inline-block',
                              padding: '8px 16px',
                              backgroundColor: theme?.colors?.primary || '#0f172a',
                              color: 'white',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              fontWeight: '500',
                              fontSize: '13px',
                              textAlign: 'center',
                              boxShadow: `0 2px 8px ${theme?.colors?.primary ? `${theme.colors.primary}33` : 'rgba(15, 23, 42, 0.2)'}`
                            }}
                          >
                            Watch Full Short
                          </a>
                          
                          {/* Preview Toggle Button */}
                          {videoId && (
                            <button
                              onClick={() => setShortsAutoPlay(!shortsAutoPlay)}
                              style={{
                                padding: '8px 12px',
                                backgroundColor: shortsAutoPlay ? '#ef4444' : (theme?.colors?.primary || '#0f172a'),
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              {shortsAutoPlay ? (
                                <>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                                    <path d="M6 6h4v12H6zM14 6h4v12h-4z"/>
                                  </svg>
                                  Stop
                                </>
                              ) : (
                                <>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                  Preview
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Lecture Clips content — Drive video player */}
              {modalContent?.type === 'lecture-clips' && modalContent?.data && (
                <div>
                  {(() => {
                    const currentClip = Array.isArray(modalContent?.data)
                      ? modalContent?.data?.[currentClipIndex]
                      : modalContent?.data;
                    const driveFileId = currentClip?.driveFileId;

                    return (
                      <>
                        {/* Video preview */}
                        <div style={{
                          marginBottom: '12px',
                          marginLeft: '-20px',
                          marginRight: '-20px',
                          marginTop: '-20px',
                          borderRadius: '0 0 12px 12px',
                          overflow: 'hidden',
                          backgroundColor: '#000',
                          position: 'relative',
                          height: window.innerWidth <= 768 ? '320px' : '380px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {driveFileId ? (
                            <video
                              key={driveFileId}
                              autoPlay
                              playsInline
                              muted
                              controls
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                              src={buildDriveStreamUrl(driveFileId)}
                            />
                          ) : (
                            <div style={{
                              width: '100%', height: '100%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              backgroundColor: '#1f2937'
                            }}>
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                                <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>
                              </svg>
                            </div>
                          )}

                          {/* CLIP badge */}
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            background: 'rgba(0, 0, 0, 0.6)',
                            backdropFilter: 'blur(10px)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z"/>
                            </svg>
                            CLIP
                          </div>
                        </div>

                        {/* Title */}
                        <h4 style={{
                          fontSize: '16px', fontWeight: '600', marginBottom: '6px',
                          color: '#1f2937', lineHeight: '1.2',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                        }}>
                          {currentClip?.title || 'Untitled Clip'}
                        </h4>

                        {/* Speaker and Series */}
                        {(currentClip?.speakerName || currentClip?.series) && (
                          <p style={{
                            fontSize: '13px', color: '#6b7280', marginBottom: '10px',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}>
                            {currentClip?.speakerName && <span>{currentClip.speakerName}</span>}
                            {currentClip?.speakerName && currentClip?.series && ' • '}
                            {currentClip?.series && <span>{currentClip.series}</span>}
                          </p>
                        )}

                        {/* Action button */}
                        <a
                          href={`/lecture-clips?v=${encodeURIComponent(driveFileId || '')}`}
                          onClick={() => setShowModal(false)}
                          style={{
                            display: 'inline-block', padding: '8px 16px',
                            backgroundColor: theme?.colors?.primary || '#0f172a',
                            color: 'white', borderRadius: '6px', textDecoration: 'none',
                            fontWeight: '500', fontSize: '13px', textAlign: 'center',
                            boxShadow: `0 2px 8px ${theme?.colors?.primary ? `${theme.colors.primary}33` : 'rgba(15, 23, 42, 0.2)'}`
                          }}
                        >
                          Open Full Player
                        </a>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Insight content */}
              {modalContent?.type === 'insight' && modalContent?.data && (
                <div>
                  <div style={{
                    fontSize: '17px',
                    lineHeight: '1.7',
                    color: '#1f2937',
                    marginBottom: '20px',
                    fontStyle: 'italic',
                    backgroundColor: '#fef3c7',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #fbbf24'
                  }}>
                    💡 "{modalContent?.data?.text}"
                  </div>
                  
                  <div style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    marginBottom: '16px',
                    fontStyle: 'italic'
                  }}>
                    from Lectures at Dar-e-Zainab
                  </div>

                  <a
                    href={`/lecture/${modalContent?.data?.lectureId}`}
                    onClick={() => setShowModal(false)}
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      backgroundColor: theme?.colors?.primary || '#0f172a',
                      color: 'white',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '600',
                      width: '100%',
                      textAlign: 'center'
                    }}
                  >
                    Explore Full Lecture →
                  </a>
                </div>
              )}

              {/* Live event content */}
              {modalContent?.type === 'live' && modalContent?.data && (
                <div>
                  <h4 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#1f2937'
                  }}>
                    {modalContent?.data?.title || ''}
                  </h4>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '16px',
                    color: '#dc2626',
                    fontWeight: '500'
                  }}>
                    <span>🔴</span>
                    <span>Happening Now!</span>
                  </div>
                  {modalContent?.data?.flyer_url && (
                    <img
                      src={modalContent?.data?.flyer_url}
                      alt={modalContent?.data?.title || ''}
                      style={{
                        width: '100%',
                        borderRadius: '8px',
                        marginBottom: '16px'
                      }}
                    />
                  )}
                  <div 
                    style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: '#374151',
                      marginBottom: '16px'
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: (modalContent?.data?.description || '')
                        .replace(/\n/g, '<br />')
                        .replace(/\r\n/g, '<br />')
                        .replace(/\\n/g, '<br />')
                    }}
                  />
                  {modalContent?.data?.location && (
                    <p style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      marginBottom: '8px'
                    }}>
                      📍 {modalContent?.data?.location}
                    </p>
                  )}
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280'
                  }}>
                    ⏰ {modalContent?.data?.start_time} - {modalContent?.data?.end_time}
                  </p>
                </div>
              )}

              {/* Video content */}
              {modalContent?.type === 'video' && modalContent?.data && (
                <div>
                  <h4 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#1f2937'
                  }}>
                    {modalContent?.data?.title || ''}
                  </h4>
                  {modalContent?.data?.thumbnail && (
                    <div style={{
                      position: 'relative',
                      marginBottom: '16px'
                    }}>
                      <img
                        src={modalContent?.data?.thumbnail}
                        alt={modalContent?.data?.title || ''}
                        style={{
                          width: '100%',
                          borderRadius: '8px'
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '60px',
                        height: '60px',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}>
                        <div style={{
                          width: 0,
                          height: 0,
                          borderLeft: '20px solid white',
                          borderTop: '12px solid transparent',
                          borderBottom: '12px solid transparent',
                          marginLeft: '4px'
                        }} />
                      </div>
                    </div>
                  )}
                  <div 
                    style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: '#374151',
                      marginBottom: '16px'
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: (modalContent?.data?.description || '')
                        .replace(/\n/g, '<br />')
                        .replace(/\r\n/g, '<br />')
                        .replace(/\\n/g, '<br />')
                    }}
                  />
                  <a
                    href={`/videos/${modalContent?.data?.id}`}
                    style={{
                      display: 'inline-block',
                      padding: '8px 16px',
                      backgroundColor: theme?.colors?.primary || '#0f172a',
                      color: 'white',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Watch Video →
                  </a>
                </div>
              )}

              {/* Majlis Insights content - One at a time */}
              {modalContent?.type === 'insights' && modalContent?.data && Array.isArray(modalContent.data) && modalContent?.data?.[currentInsightIndex] && (
                <div 
                  id={`insight-card-${currentInsightIndex}`}
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    height: '100%',
                    maxHeight: 'calc(90vh - 100px)',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  {/* Content container - no scroll */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      padding: '0 20px',
                      overflow: 'hidden',
                      flex: 1
                    }}>
                      {/* Quote/Insight - controlled height */}
                      <div style={{
                        fontSize: '16px',
                        lineHeight: '1.6',
                        color: '#374151',
                        marginBottom: '16px',
                        fontStyle: 'italic',
                        borderLeft: `4px solid ${theme?.colors?.primary || '#0f172a'}`,
                        paddingLeft: '16px',
                        maxHeight: '60%',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 7,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        "{modalContent?.data?.[currentInsightIndex].takeaway}"
                      </div>
                      
                      {/* Lecture info - compact */}
                      {modalContent?.data?.[currentInsightIndex].lecture_title && (
                        <div style={{
                          fontSize: '13px',
                          color: '#6b7280',
                          marginBottom: '16px',
                          flexShrink: 0
                        }}>
                          <div>
                            <strong>{modalContent?.data?.[currentInsightIndex].lecture_title}</strong>
                            {modalContent?.data?.[currentInsightIndex].speaker && 
                              ` • ${modalContent?.data?.[currentInsightIndex].speaker}`}
                          </div>
                          {modalContent?.data?.[currentInsightIndex].series && (
                            <div style={{ fontSize: '12px', marginTop: '2px' }}>
                              {modalContent?.data?.[currentInsightIndex].series}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Spacer to push buttons down */}
                      <div style={{ flex: 1 }} />
                      
                      {/* Action Buttons Container */}
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        {/* Link to lecture */}
                        {modalContent?.data?.[currentInsightIndex].lecture_id && (
                          <a
                            href={`/lecture/${modalContent?.data?.[currentInsightIndex].lecture_id}`}
                            onClick={() => setShowModal(false)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '10px 20px',
                              backgroundColor: theme?.colors?.primary || '#0f172a',
                              color: 'white',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              fontSize: '14px',
                              fontWeight: '600',
                              transition: 'all 0.2s',
                              flex: 1
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = theme?.colors?.primary ? 
                                `${theme.colors.primary}dd` : '#1e293b';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = theme?.colors?.primary || '#0f172a';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            View Lecture →
                          </a>
                        )}
                      </div>
                      
                      {/* Navigation hint - compact */}
                      <div style={{
                        fontSize: '11px',
                        color: '#9ca3af',
                        textAlign: 'center',
                        paddingBottom: '8px',
                        flexShrink: 0
                      }}>
                        Swipe to navigate insights
                      </div>
                    </div>
                </div>
              )}

              {/* Short/Quick clip content */}
              {modalContent?.type === 'short' && modalContent?.data && (
                <div>
                  <h4 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#1f2937'
                  }}>
                    {modalContent.data.title || 'Quick Clip'}
                  </h4>
                  {modalContent?.data?.thumbnail && (
                    <div style={{
                      position: 'relative',
                      marginBottom: '16px',
                      paddingBottom: '177.78%', // 9:16 aspect ratio
                      backgroundColor: '#000',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <img
                        src={modalContent?.data?.thumbnail}
                        alt={modalContent?.data?.title || ''}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '60px',
                        height: '60px',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}>
                        <div style={{
                          width: 0,
                          height: 0,
                          borderLeft: '20px solid white',
                          borderTop: '12px solid transparent',
                          borderBottom: '12px solid transparent',
                          marginLeft: '4px'
                        }} />
                      </div>
                    </div>
                  )}
                  <a
                    href='/shorts'
                    style={{
                      display: 'inline-block',
                      padding: '8px 16px',
                      backgroundColor: '#8b5cf6',
                      color: 'white',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Watch Short →
                  </a>
                </div>
              )}

              {/* Lecture content */}
              {modalContent?.type === 'lecture' && modalContent?.data && (
                <div>
                  <h4 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: '#1f2937'
                  }}>
                    {modalContent?.data?.title || ''}
                  </h4>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    marginBottom: '16px'
                  }}>
                    By {modalContent?.data?.speaker || 'Speaker'}
                  </p>
                  {modalContent?.data?.thumbnail && (
                    <img
                      src={modalContent?.data?.thumbnail}
                      alt={modalContent?.data?.title || ''}
                      style={{
                        width: '100%',
                        borderRadius: '8px',
                        marginBottom: '16px'
                      }}
                    />
                  )}
                  <div 
                    style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: '#374151',
                      marginBottom: '16px'
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: (modalContent?.data?.description || 'Join us for this insightful lecture.')
                        .replace(/\n/g, '<br />')
                        .replace(/\r\n/g, '<br />')
                        .replace(/\\n/g, '<br />')
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    gap: '8px'
                  }}>
                    <a
                      href={`/lecture/${modalContent?.data?.id}`}
                      onClick={() => {
                        setShowModal(false);
                      }}
                      style={{
                        display: 'inline-block',
                        padding: '8px 16px',
                        backgroundColor: theme?.colors?.primary || '#0f172a',
                        color: 'white',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      View Lecture →
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Image Modal for Fullscreen View */}
      {selectedImage && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2147483650,
            cursor: 'zoom-out',
            padding: '20px'
          }}
          onClick={() => setSelectedImage(null)}
        >
          <img 
            src={selectedImage} 
            alt="Full size view"
            style={{
              maxWidth: '90%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button 
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontSize: '24px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onClick={() => setSelectedImage(null)}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            ×
          </button>
        </div>,
        document.body
      )}

      {/* PDF Viewer Modal - Rendered via portal to ensure proper z-index stacking */}
      {pdfUrl && pdfAnnouncement && createPortal(
        <PDFViewer 
          pdfUrl={pdfUrl}
          title={pdfAnnouncement.title || 'PDF Document'}
          author={pdfAnnouncement.author || ''}
          onClose={() => {
            setPdfUrl(null);
            setPdfAnnouncement(null);
            console.log('[StoriesBar] PDF viewer closed');
          }}
        />,
        document.body
      )}

      {/* Add animations */}
      <style>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(220, 38, 38, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
          }
        }
        
        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        /* Hide scrollbar */
        .stories-container::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default StoriesBar;